/*
 * My Templates — React state over myBrandTemplatesStorage's localStorage CRUD,
 * plus the short-lived "Saved…" toast shown after a successful save. Mirrors
 * useShareExport's toast-timeout pattern.
 */
import { useCallback, useState } from "react";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { useTimedToast } from "../../shared/hooks/useTimedToast";
import {
  listMyBrandTemplates,
  logMyBrandTemplatesError,
  removeMyBrandTemplate,
  saveMyBrandTemplate,
  type MyBrandTemplate,
} from "../services/myBrandTemplatesStorage";
import type { BrandSpec } from "../../../types";

const TOAST_DURATION_MS = 3500;
const SAVE_TOAST_MESSAGE = "Saved. Now listed under Saved Templates · My Templates.";
const SAVE_FAILED_MESSAGE = "Couldn't save this template locally.";

export interface UseMyBrandTemplates {
  readonly templates: readonly MyBrandTemplate[];
  readonly toast: string | null;
  /** Persists `brand` under `name`; returns false (and shows an error toast)
   *  if saving failed rather than throwing into the caller's click handler. */
  readonly save: (name: string, brand: BrandSpec, colors: readonly string[]) => boolean;
  readonly remove: (id: string) => void;
}

export function useMyBrandTemplates(): UseMyBrandTemplates {
  const [templates, setTemplates] = useState<readonly MyBrandTemplate[]>(() => listMyBrandTemplates());
  const { toast, showToast } = useTimedToast(TOAST_DURATION_MS);

  const save = useCallback((name: string, brand: BrandSpec, colors: readonly string[]) => {
    try {
      const entry = saveMyBrandTemplate(name, brand, colors);
      setTemplates((prev) => [entry, ...prev]);
      showToast(SAVE_TOAST_MESSAGE);
      return true;
    } catch (error) {
      logMyBrandTemplatesError("my-brand-templates-save-toast", getErrorMessage(error, SAVE_FAILED_MESSAGE));
      showToast(SAVE_FAILED_MESSAGE);
      return false;
    }
  }, [showToast]);

  const remove = useCallback((id: string) => {
    removeMyBrandTemplate(id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { templates, toast, save, remove };
}
